import { StandardEvents } from '@shopify/events';
let termsAccepted = false;
const recommendationCache = new Map();

class MemioCartBuy extends HTMLElement {
  connectedCallback() {
    this.onChange = () => {
      const select = this.querySelector('[data-memio-variant]');
      const option = select?.selectedOptions[0];
      const form = this.querySelector('product-form-component');
      if (!option || !form) return;
      form.querySelector('[name="id"]').value = option.value;
      form.querySelector('[name="quantity"]').value = option.dataset.min || '1';
      form.dataset.quantityDefault = option.dataset.min || '1';
      const price = this.querySelector('[data-memio-price]');
      const compare = this.querySelector('[data-memio-compare]');
      if (price) price.textContent = option.dataset.price;
      if (compare) { compare.textContent = option.dataset.compare || ''; compare.hidden = !option.dataset.compare; }
      const scope = this.closest('[data-offer-product], [data-memio-product-result]');
      const image = scope?.querySelector('.memio-cart__variant-image');
      if (image && option.dataset.image) {
        image.srcset = option.dataset.imageSrcset || '';
        image.src = option.dataset.image;
        if (this.closest('[data-memio-product-result]')) image.alt = option.dataset.imageAlt || '';
      }
      form.querySelector('button[type="submit"]').disabled = option.disabled;
    };
    this.addEventListener('change', this.onChange);
  }
  disconnectedCallback() { this.removeEventListener('change', this.onChange); }
}

class MemioCartRecommendations extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
    }, { rootMargin: '100px' });
    this.observer.observe(this);
  }
  disconnectedCallback() { this.observer?.disconnect(); this.abort?.abort(); }
  async load() {
    const key = this.dataset.url + '|' + this.dataset.cartProducts;
    try {
      let html = recommendationCache.get(key);
      if (!html) {
        const response = await fetch(this.dataset.url, { signal: this.abort.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        html = await response.text();
        recommendationCache.set(key, html);
        if (recommendationCache.size > 12) recommendationCache.delete(recommendationCache.keys().next().value);
      }
      if (!this.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const result = doc.querySelector('[data-memio-recommendations-result]');
      if (!result) throw new Error('Missing recommendations');
      const excluded = new Set((this.dataset.cartProducts || '').split(','));
      result.querySelectorAll('[data-offer-product]').forEach(card => {
        if (excluded.has(card.dataset.offerProduct)) card.remove();
      });
      this.replaceChildren(result);
      this.closest('[data-memio-offers]')?.toggleAttribute('hidden', !this.querySelector('[data-offer-product]'));
    } catch (error) {
      if (error.name !== 'AbortError') this.closest('[data-memio-offers]')?.setAttribute('hidden', '');
    }
  }
}

class MemioCartTools extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.addEventListener('click', event => this.click(event), { signal });
    this.addEventListener('change', event => {
      if (event.target.matches('[data-memio-terms]')) {
        termsAccepted = event.target.checked;
        this.querySelector('.memio-cart__terms-error')?.setAttribute('hidden', '');
      }
    }, { signal });
    this.addEventListener('submit', event => {
      if (event.target.id === 'cart-form' && !this.checkTerms()) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { capture: true, signal });
    this.addEventListener('invalid', event => {
      if (event.target.matches('[data-memio-terms]')) this.querySelector('.memio-cart__terms-error')?.removeAttribute('hidden');
    }, { capture: true, signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      const modalSource = event.target.closest?.('#MemioBuy-modal');
      event.promise?.then(result => {
        if (!this.isConnected) return;
        if (modalSource && !result.detail?.didError) this.closeModal(true);
        requestAnimationFrame(() => this.sync());
      }).catch(() => {});
    }, { signal });
    this.resize = new ResizeObserver(entries => {
      const size = entries[0]?.borderBoxSize?.[0]?.blockSize || entries[0]?.contentRect.height || 0;
      this.style.setProperty('--memio-footer-height', `${Math.ceil(size)}px`);
    });
    this.mutations = new MutationObserver(() => this.sync());
    this.mutations.observe(this, { childList: true, subtree: true });
    this.sync();
    this.modal = this.querySelector('[data-memio-variant-dialog]');
    this.modal?.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.closeModal(); }
    }, { signal });
    this.modal?.addEventListener('cancel', event => { event.preventDefault(); this.closeModal(); }, { signal });
    this.modal?.addEventListener('click', event => { if (event.target === this.modal) this.closeModal(); }, { signal });
    document.addEventListener('pointerdown', event => {
      this.querySelectorAll('.memio-cart__announcement details[open]').forEach(el => {
        if (!el.contains(event.target)) el.open = false;
      });
    }, { signal });
  }
  disconnectedCallback() {
    this.abort?.abort(); this.modalAbort?.abort(); this.resize?.disconnect(); this.mutations?.disconnect();
  }
  sync() {
    const checkbox = this.querySelector('[data-memio-terms]');
    if (checkbox && checkbox.checked !== termsAccepted) checkbox.checked = termsAccepted;
    const footer = this.querySelector('.memio-cart__footer');
    if (footer !== this.footer) { this.resize.disconnect(); this.footer = footer; if (footer) this.resize.observe(footer); }
    this.querySelectorAll('[data-memio-offers]').forEach(group => {
      if (!group.querySelector('memio-cart-recommendations')) group.hidden = !group.querySelector('[data-offer-product]');
    });
  }
  checkTerms() {
    const checkbox = this.querySelector('[data-memio-terms]');
    if (!checkbox || checkbox.checked) return true;
    this.querySelector('.memio-cart__terms-error')?.removeAttribute('hidden');
    checkbox.focus(); checkbox.reportValidity(); return false;
  }
  click(event) {
    const checkout = event.target.closest('.cart__checkout-button');
    if (checkout && !this.checkTerms()) { event.preventDefault(); event.stopPropagation(); return; }
    const open = event.target.closest('[data-memio-modal-url]');
    if (open) { event.preventDefault(); this.openModal(open); return; }
    if (event.target.closest('[data-memio-close-modal]')) { this.closeModal(); return; }
    const arrow = event.target.closest('[data-memio-scroll]');
    if (arrow) {
      const list = arrow.closest('[data-memio-offers]').querySelector('.memio-cart__offer-list');
      list?.scrollBy({ left: Number(arrow.dataset.memioScroll) * list.clientWidth * .85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  }
  async openModal(button) {
    if (!this.modal) return;
    this.opener = button;
    this.modalAbort?.abort(); this.modalAbort = new AbortController();
    const content = this.modal.querySelector('[data-memio-modal-content]');
    content.replaceChildren();
    const status = document.createElement('p'); status.textContent = this.dataset.loading; status.setAttribute('role', 'status'); content.append(status);
    if (!this.modal.open) this.modal.showModal();
    this.modal.querySelector('[data-memio-close-modal]').focus();
    try {
      const url = new URL(button.dataset.memioModalUrl, location.origin); url.searchParams.set('section_id', 'memio-cart-product');
      const response = await fetch(url, { signal: this.modalAbort.signal });
      if (!response.ok) throw new Error('Product unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = doc.querySelector('[data-memio-product-result]');
      if (!result?.querySelector('[name="id"]')) throw new Error('Missing product');
      if (button.dataset.memioAddon === 'true') {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = 'properties[_memio_addon]'; input.value = 'true'; result.querySelector('form').append(input);
      }
      content.replaceChildren(...result.childNodes);
      content.querySelector('select,button')?.focus();
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.textContent = this.dataset.error;
      const link = document.createElement('a'); link.href = button.dataset.memioModalUrl; link.textContent = this.dataset.view; content.append(link);
    }
  }
  closeModal(success = false) {
    this.modalAbort?.abort(); this.modal?.close();
    const focus = !success && this.opener?.isConnected ? this.opener : this.querySelector('.theme-drawer__close-button');
    focus?.focus();
  }
}
if (!customElements.get('memio-cart-buy')) customElements.define('memio-cart-buy', MemioCartBuy);
if (!customElements.get('memio-cart-recommendations')) customElements.define('memio-cart-recommendations', MemioCartRecommendations);
if (!customElements.get('memio-cart-tools')) customElements.define('memio-cart-tools', MemioCartTools);
